import {
	GetWorkflowTasksPendingEndPoint,
	SearchWorkflowTasksPendingEndPoint,
	GetWorkflowTasksUnassignedEndPoint,
	SearchWorkflowTasksUnassignedEndPoint,
	ExecuteWorkflowTaskEndPoint,
	UnclaimWorkflowTaskEndPoint,
	ClaimWorkflowTaskEndPoint,
	DeleteWorkflowProcessEndPoint,
	ProcessDocumentsEndPoint,
	ProcessDocumentDownloadEndPoint,
	RegenerateProcessDocumentEndPoint,
	DocumentTemplateVersionsEndPoint,
	ReassignCandidatesEndPoint,
	ReassignTaskEndPoint,
	GetMyWorkflowProcessesEndPoint,
	SearchMyWorkflowProcessesEndPoint,
	CloneWorkflowProcessEndPoint,
	StartWorkflowProcessEndPoint,
	ViewWorkflowProcessEndPoint,
	ClaimTaskEndPoint,
	GetWorkflowsEndpoint,
	StartProcessEndPoint,
} from "common/constants";
import { makeAuthenticatedRequest } from "../helpers/authenticatedRequest";

// Documents generated so far for a process (any status). The backend checks download_pdf on the
// process's workflow in the company.
export const getProcessDocuments = async ({ processInstanceId, companyId }, signal = null) => {
	const response = await makeAuthenticatedRequest(ProcessDocumentsEndPoint, {
		method: "POST",
		body: JSON.stringify({ processInstanceId, companyId }),
		signal,
	});

	return response;
};

// A short-lived (60 s) signed link to download one document from the private bucket, after the
// same access check. Use it right away; don't store it.
export const getProcessDocumentDownload = async ({ documentId, companyId }, signal = null) => {
	const response = await makeAuthenticatedRequest(ProcessDocumentDownloadEndPoint, {
		method: "POST",
		body: JSON.stringify({ documentId, companyId }),
		signal,
	});

	return response;
};

// Regenerates one document as a new version (the previous file is kept), with the chosen version of
// its template (templateVersionId; omitted = the version it uses). The backend checks regenerate_pdf
// on the document's workflow in the company, and that the version belongs to the same template.
export const regenerateProcessDocument = async ({ documentId, companyId, templateVersionId }, signal = null) => {
	return await makeAuthenticatedRequest(RegenerateProcessDocumentEndPoint, {
		method: "POST",
		body: JSON.stringify({ documentId, companyId, templateVersionId: templateVersionId || undefined }),
		signal,
	});
};

// The versions of a document's template, newest first, with the one it uses marked (Regenerate's
// choices). Same access check as Regenerate.
export const getDocumentTemplateVersions = async ({ documentId, companyId }, signal = null) => {
	return await makeAuthenticatedRequest(DocumentTemplateVersionsEndPoint, {
		method: "POST",
		body: JSON.stringify({ documentId, companyId }),
		signal,
	});
};

// The process's open task and the users it can be reassigned to (those who could execute it).
// The backend checks reassign_task on the process's workflow in the company.
export const getReassignCandidates = async ({ processInstanceId, companyId }, signal = null) => {
	return await makeAuthenticatedRequest(ReassignCandidatesEndPoint, {
		method: "POST",
		body: JSON.stringify({ processInstanceId, companyId }),
		signal,
	});
};

// One of the caller's processes, read-only: one step per form the caller's roles grant, with the
// saved answers. The backend checks membership, the workflow and the forms in the company.
export const viewWorkflowProcess = async ({ processInstanceId, companyId }, signal = null) => {
	return await makeAuthenticatedRequest(ViewWorkflowProcessEndPoint, {
		method: "POST",
		body: JSON.stringify({ processInstanceId, companyId }),
		signal,
	});
};

// Starts a new process of a workflow in the company (caller = starter). The backend checks
// start_workflow and records the company on the process.
export const startWorkflowProcessInCompany = async ({ processDefinitionKey, companyId }, signal = null) => {
	return await makeAuthenticatedRequest(StartWorkflowProcessEndPoint, {
		method: "POST",
		body: JSON.stringify({ processDefinitionKey, companyId }),
		signal,
	});
};

// Starts a new process pre-filled from a source process's answers (fields marked clone = true in
// the form). Source: processInstanceId (one of the caller's processes) OR processDefinitionKey (the
// company's latest process of that workflow). The backend checks clone_workflow.
export const cloneWorkflowProcess = async ({ processInstanceId, processDefinitionKey, companyId }, signal = null) => {
	return await makeAuthenticatedRequest(CloneWorkflowProcessEndPoint, {
		method: "POST",
		body: JSON.stringify({ processInstanceId, processDefinitionKey, companyId }),
		signal,
	});
};

// Gives an open task to another user (one of the candidates; checked again by the backend).
export const reassignTask = async ({ taskId, newAssignee, companyId }, signal = null) => {
	return await makeAuthenticatedRequest(ReassignTaskEndPoint, {
		method: "POST",
		body: JSON.stringify({ taskId, newAssignee, companyId }),
		signal,
	});
};

// Deletes (cancels) a whole process. The backend decides who may, from the caller (token), the
// workflow's delete_workflow permission in the company and its delete policy, so only the process
// and the company are sent.
export const deleteWorkflowProcess = async ({ processInstanceId, companyId }, signal = null) => {
	const response = await makeAuthenticatedRequest(DeleteWorkflowProcessEndPoint, {
		method: "POST",
		body: JSON.stringify({ processInstanceId, companyId }),
		signal,
	});

	return response;
};

export const getWorkflowsList = async (userId, companyId, signal = null) => {
	const url = `${GetWorkflowsEndpoint}?userId=${userId}&companyId=${companyId}`;
	const response = await makeAuthenticatedRequest(url, { signal });
	return response;
};

// request: a PendingTasksRequest (dto/workflowDto) plus companyId (added by the saga); the search
// endpoint also gets lang. The backend always uses the logged-in user (token) as the assignee.
// Plain list: raw-column sorts, no search text.
export const getPendingTasks = async (request, signal = null) => {
	const result = await makeAuthenticatedRequest(GetWorkflowTasksPendingEndPoint, {
		method: "POST",
		body: JSON.stringify(request),
		signal,
	});

	return result;
};

// Same request, through the search RPC: search text and sorting by translated label.
export const searchPendingTasks = async (request, signal = null) => {
	const result = await makeAuthenticatedRequest(SearchWorkflowTasksPendingEndPoint, {
		method: "POST",
		body: JSON.stringify(request),
		signal,
	});

	return result;
};

// Unassigned tasks: same request as pending tasks (PendingTasksRequest plus companyId; lang for the
// search endpoint). The backend matches the tasks' candidates against the logged-in user (token)
// and their roles in the company. Plain list: raw-column sorts, no search text.
export const getUnassignedTasks = async (request, signal = null) => {
	return await makeAuthenticatedRequest(GetWorkflowTasksUnassignedEndPoint, {
		method: "POST",
		body: JSON.stringify(request),
		signal,
	});
};

// Same request, through the search RPC: search text and sorting by translated label.
export const searchUnassignedTasks = async (request, signal = null) => {
	return await makeAuthenticatedRequest(SearchWorkflowTasksUnassignedEndPoint, {
		method: "POST",
		body: JSON.stringify(request),
		signal,
	});
};

// request: a MyProcessesRequest (dto/workflowDto) plus companyId (added by the saga); the search
// endpoint also gets lang. The backend always uses the logged-in user (token).
// Plain list: raw-column sorts, no search text.
export const getMyProcesses = async (request, signal = null) => {
	return await makeAuthenticatedRequest(GetMyWorkflowProcessesEndPoint, {
		method: "POST",
		body: JSON.stringify(request),
		signal,
	});
};

// Same request, through the search RPC: search text and sorting by translated label.
export const searchMyProcesses = async (request, signal = null) => {
	return await makeAuthenticatedRequest(SearchMyWorkflowProcessesEndPoint, {
		method: "POST",
		body: JSON.stringify(request),
		signal,
	});
};

// The backend checks the caller is the assignee (from the token) and may unclaim in the company,
// so only the task and the company are sent.
export const unclaimPendingTask = async ({ taskId, companyId }, signal = null) => {
	const response = await makeAuthenticatedRequest(UnclaimWorkflowTaskEndPoint, {
		method: "POST",
		body: JSON.stringify({ taskId, companyId }),
		signal,
	});

	return response;
};

// Unassigned tasks page: claims the task for the logged-in user (token; no user id is sent) and
// returns its Execute steps. The backend checks the company, that nobody has the task, claim_task,
// that it's offered to the user, and the form, before claiming.
export const claimUnassignedTask = async ({ taskId, companyId }, signal = null) => {
	return await makeAuthenticatedRequest(ClaimWorkflowTaskEndPoint, {
		method: "POST",
		body: JSON.stringify({ taskId, companyId }),
		signal,
	});
};

export const claimTask = async (taskID, userID, signal = null) => {
	var endpoint = `${ClaimTaskEndPoint}/${taskID}/${userID}`;

	const response = await makeAuthenticatedRequest(endpoint, {
		method: "POST",
		signal,
	});

	return response;
};

export const startWorkflowProcess = async (definitionKey, assignee, signal = null) => {
	var endpoint = `${StartProcessEndPoint}/${definitionKey}/${assignee}`;

	const response = await makeAuthenticatedRequest(endpoint, {
		method: "POST",
		signal,
	});

	return response;
};

// The backend finds the caller's open task and resolves their roles/forms in the company itself,
// so only the process and the company are sent (no task id, no roles).
export const executePendingTask = async ({ processInstanceId, companyId }, signal = null) => {
	const response = await makeAuthenticatedRequest(ExecuteWorkflowTaskEndPoint, {
		method: "POST",
		body: JSON.stringify({ processInstanceId, companyId }),
		signal,
	});

	return response;
};
